import { jwtDecode } from 'jwt-decode';

interface JwtPayload {
  sub?: string;
  email?: string;
  name?: string;
  exp?: number;
}

/** Dispatched when a stored JWT is found to be expired/invalid. AuthContext listens for it. */
export const AUTH_EXPIRED_EVENT = 'auth:expired';

export const isAuthExpiredError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;
  if (err.__authExpired === true) return true;
  const response = err.response as { status?: number } | undefined;
  return response?.status === 401;
};

export const createAuthExpiredError = (message = 'Token expired'): Error & { __authExpired: boolean } => {
  const error = new Error(message) as Error & { __authExpired: boolean };
  error.__authExpired = true;
  return error;
};

export const isTokenExpired = (token: string | null | undefined): boolean => {
  if (!token) return true;
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    if (!decoded.exp) return false;
    return decoded.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

/** Returns the stored token only if it is still valid, otherwise null. */
export const getValidToken = (): string | null => {
  const token = localStorage.getItem('token');
  if (!token || isTokenExpired(token)) return null;
  return token;
};

/**
 * Clears an expired/invalid token and notifies the app immediately,
 * so the login screen shows without waiting for a slow API round-trip.
 */
export const handleExpiredToken = (): void => {
  localStorage.removeItem('token');
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
};
